import type { ReactNode } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useLimitedOfferStock } from '../../../../../hooks/useLimitedOfferStock';
import type { EventTriggerPopupData } from '../../../../../stores/event-trigger/types';
import { WidgetStockCounter } from '../WidgetStockCounter/WidgetStockCounter';

import styles from './LimitedOfferSmallWidget.module.scss';

interface LimitedOfferSmallWidgetProps {
  event: EventTriggerPopupData;
  widgetImageUrl: string;
  timer?: ReactNode;
}

/**
 * The small lobby widget of a Limited Offer: its stock sits above the timer in a capsule over the
 * artwork.
 *
 * A dedicated component rather than a slot on GeneralSmallWidget — the layout differs from the shared
 * one in more than an extra row, and no other widget wants either the capsule or the stock.
 *
 * An observer because it reads the stock for the sold-out colours: the flip arrives on the
 * event-trigger tick, and nothing else in this subtree would re-render on it.
 */
export const LimitedOfferSmallWidget = observer((props: LimitedOfferSmallWidgetProps) => {
  const { event, widgetImageUrl, timer } = props;
  const limitedOfferStock = useLimitedOfferStock(event);

  return (
    <div className={styles.container}>
      <img alt="limited offer widget" className={styles.img} src={widgetImageUrl} />
      <div className={styles.content}>
        <div className={styles.stockRow}>
          <WidgetStockCounter event={event} />
        </div>
        <div className={classNames(styles.timerRow, { [styles.timerRowSoldOut]: limitedOfferStock?.isSoldOut })}>
          {timer}
        </div>
      </div>
    </div>
  );
});
